/**
 * Sprint 23.8 — WorkspaceScorecard (hero section 6).
 *
 * Workspace-wide "this week" via the shared `<MetricScorecard>` (same primitive
 * the agent profile uses). Reads `scorecard` off the batched overview so it
 * doesn't issue its own poll. Brand-new workspaces (≤7 days old) show zeros +
 * "—" deltas + a tiny caption (Q / S23.8.50) instead of looking broken.
 */

import { type CSSProperties } from "react";
import { MetricScorecard } from "../scorecard/MetricScorecard";
import type { MetricCardProps } from "../scorecard/MetricCard";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import { useWorkspaceOverview } from "../../hooks/use-workspace-overview";
import { formatMoney } from "./format";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

type MetricKey = "tasks_done" | "retros" | "learnings" | "ai_spend";

interface MetricSpec {
  key: MetricKey;
  label: string;
  isMoney?: boolean;
}

const METRICS: MetricSpec[] = [
  { key: "tasks_done", label: "Tasks done" },
  { key: "retros", label: "Retros" },
  { key: "learnings", label: "Learnings" },
  { key: "ai_spend", label: "AI spend", isMoney: true },
];

function formatSigned(n: number, isMoney: boolean): string {
  if (isMoney) return `${n >= 0 ? "+" : "-"}$${Math.abs(n).toFixed(2)}`;
  return n > 0 ? `+${n}` : `${n}`;
}

export function WorkspaceScorecard() {
  const labels = useThemedLabels();
  const query = useWorkspaceOverview();
  const data = query.data;
  const scorecard = data?.scorecard;

  // Brand-new workspace → caption (Q / S23.8.50).
  const createdAt = data?.workspace?.created_at;
  const isNew = createdAt
    ? Date.now() - new Date(createdAt).getTime() < SEVEN_DAYS_MS
    : false;

  const metrics: MetricCardProps[] = METRICS.map((m) => {
    const value = scorecard ? Number(scorecard.current[m.key] ?? 0) : 0;
    const delta = scorecard ? Number(scorecard.deltas[m.key] ?? 0) : 0;
    const hasPrior = scorecard
      ? Number(scorecard.prior[m.key] ?? 0) > 0 || delta !== 0
      : false;
    return {
      label: m.label,
      value: query.isLoading ? "…" : m.isMoney ? formatMoney(value) : String(value),
      delta: !hasPrior ? "—" : delta === 0 ? "=" : formatSigned(delta, !!m.isMoney),
      deltaTone: hasPrior && delta > 0 ? "success" : "tertiary",
      deltaTitle: hasPrior ? "vs prior 7 days" : "no prior period",
    };
  });

  return (
    <section>
      <MetricScorecard title={labels.workspaceHomeScorecardTitle} metrics={metrics} />
      {isNew && <div style={caption}>Stats will appear with more data.</div>}
    </section>
  );
}

const caption: CSSProperties = {
  marginTop: 6,
  fontSize: 10,
  color: "var(--color-text-tertiary)",
};
